import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import {
  type PieceBackfillRunFieldsFragment,
  PieceBackfillRunMode,
} from "../../generated/graphql";
import { formatRunDuration, formatRunTimestamp } from "./piece-backfill-format";
import PieceBackfillStatusChip from "./piece-backfill-status-chip";

const styles = {
  root: { mt: 5 },
  subtitle: { mt: 0.5, mb: 2 },
  table: { maxWidth: 1100 },
} satisfies Record<string, SxProps<Theme>>;

/** Props for {@link PieceBackfillHistory}. */
export type PieceBackfillHistoryProps = {
  /** The company's recent runs, newest first. */
  readonly runs: readonly PieceBackfillRunFieldsFragment[];
  /** True while the history query is in flight. */
  readonly loading: boolean;
  /** True once a company is selected; before that the panel explains itself. */
  readonly hasCompany: boolean;
};

/**
 * The placeholder that stands in for the history table. A company with runs
 * still shows the table while a refetch is in flight, so the rows do not
 * disappear each time the history is refreshed after a run finishes.
 *
 * @param params.hasCompany Whether a company is selected.
 * @param params.loading Whether the history query is in flight.
 * @param params.runCount How many runs are loaded.
 * @returns The placeholder text, or `null` when there are rows to render.
 */
const describeEmptyHistory = ({
  hasCompany,
  loading,
  runCount,
}: {
  hasCompany: boolean;
  loading: boolean;
  runCount: number;
}): string | null => {
  if (!hasCompany) {
    return "Select a company to see its runs.";
  }
  if (runCount > 0) {
    return null;
  }
  return loading
    ? "Loading…"
    : "No backfill has ever been run for this company.";
};

/**
 * The selected company's recent backfill runs, so an operator can see whether
 * this tenant has already been backfilled before starting another run.
 */
const PieceBackfillHistory = ({
  runs,
  loading,
  hasCompany,
}: PieceBackfillHistoryProps) => {
  const emptyMessage = describeEmptyHistory({
    hasCompany,
    loading,
    runCount: runs.length,
  });

  return (
    <Box sx={styles.root}>
      <Typography variant="subtitle1">Run history</Typography>
      <Typography color="text.secondary" variant="body2" sx={styles.subtitle}>
        Every dry run and execute recorded for this company, newest first.
      </Typography>

      {emptyMessage === null ? (
        <Table size="small" sx={styles.table}>
          <TableHead>
            <TableRow>
              <TableCell>Started</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested by</TableCell>
              <TableCell align="right">Packages</TableCell>
              <TableCell align="right">Pieces created</TableCell>
              <TableCell align="right">Pieces updated</TableCell>
              <TableCell align="right">Links</TableCell>
              <TableCell align="right">Took</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => (
              <TableRow key={run.uuid}>
                <TableCell>
                  {formatRunTimestamp(run.startedAt ?? run.createdAt)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      run.mode === PieceBackfillRunMode.DryRun
                        ? "dry run"
                        : "execute"
                    }
                  />
                </TableCell>
                <TableCell>
                  <PieceBackfillStatusChip status={run.status} />
                </TableCell>
                <TableCell>{run.requestedByEmail}</TableCell>
                <TableCell align="right">
                  {run.packagesProcessed} / {run.packagesTotal}
                </TableCell>
                <TableCell align="right">{run.piecesCreated}</TableCell>
                <TableCell align="right">{run.piecesUpdated}</TableCell>
                <TableCell align="right">{run.joinsCreated}</TableCell>
                <TableCell align="right">
                  {formatRunDuration(run.startedAt, run.finishedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography color="text.secondary" variant="body2">
          {emptyMessage}
        </Typography>
      )}
    </Box>
  );
};

export default PieceBackfillHistory;
